<template>
    <transition name="app-dialog">
        <div
            v-if="value"
            class="fixed inset-0 z-500 flex items-center justify-center bg-black bg-opacity-40"
            :class="[
                size === 'full' ? '' : 'p-4',
                isNested ? '' : 'backdrop-blur-overlay'
            ]"
            @click.self="requestClose"
        >
            <div
                class="app-dialog-panel relative flex flex-col bg-theme-card text-theme-base shadow-xl"
                :class="[sizeClasses, rebound ? 'animate-dialog-rebound' : '']"
            >
                <button
                    v-if="!hideClose"
                    type="button"
                    aria-label="Cerrar"
                    class="absolute z-10 w-9 h-9 flex items-center justify-center rounded-full bg-theme-danger text-white font-bold shadow-lg hover:scale-110 transition-transform"
                    :class="
                        size === 'full' ? 'top-3 right-3' : '-top-3 -right-3'
                    "
                    @click="close"
                >
                    ✕
                </button>
                <header v-if="$slots.header" class="px-6 pt-4 pb-0">
                    <slot name="header"></slot>
                </header>
                <div class="flex-1 overflow-y-auto px-6 pb-4 pt-2">
                    <slot></slot>
                </div>
                <footer v-if="$slots.footer" class="px-6 pb-4 pt-2">
                    <slot name="footer"></slot>
                </footer>
            </div>
        </div>
    </transition>
</template>

<script>
// Dialogs abiertos a la vez (ej. confirmación encima del historial del
// editor). Solo el primero desenfoca el fondo: un backdrop-filter encima de
// otro, animado, congela la página en celulares. Y el scroll del body se
// libera recién al cerrar el último.
let openDialogsCount = 0;

const SIZE_CLASSES = {
    xs: "w-full sm:w-auto max-w-xs rounded-lg max-h-90vh",
    sm: "w-full sm:w-auto sm:min-w-dialog-sm max-w-sm rounded-lg max-h-90vh",
    md: "w-full sm:w-auto sm:min-w-dialog-md max-w-lg rounded-lg max-h-90vh",
    lg: "w-full sm:w-auto sm:min-w-dialog-lg max-w-3xl rounded-lg max-h-90vh",
    xl: "w-full sm:w-auto sm:min-w-dialog-lg max-w-5xl rounded-lg max-h-90vh",
    full: "w-screen h-screen max-w-full"
};

export default {
    name: "AppDialog",
    props: {
        value: {
            type: Boolean,
            required: true
        },
        size: {
            type: String,
            default: "md",
            validator: (value) => Object.keys(SIZE_CLASSES).includes(value)
        },
        persistent: {
            type: Boolean,
            default: false
        },
        hideClose: {
            type: Boolean,
            default: false
        },
        // Para dialogs montados dentro de un contenedor que crea su propio
        // stacking context (position: sticky/fixed, transform): ahí el
        // z-index del overlay se resuelve dentro de ese contexto y el
        // dialog queda tapado por el resto de la página. Con esto el
        // overlay se mueve al body al abrirse.
        appendToBody: {
            type: Boolean,
            default: false
        },
        // Por default el ancho del dialog se ajusta al contenido (hasta
        // max-w-*), así que cambia entre distintas vistas dentro del mismo
        // dialog (ej. tabs con contenido de distinto ancho). Con esto el
        // dialog siempre toma el ancho máximo de su `size`.
        fixedWidth: {
            type: Boolean,
            default: false
        }
    },
    emits: ["input", "close"],
    data: () => ({
        rebound: false,
        reboundTimer: null,
        isCounted: false,
        isNested: false
    }),
    computed: {
        sizeClasses() {
            const classes = SIZE_CLASSES[this.size] || SIZE_CLASSES.md;
            return this.fixedWidth
                ? classes.replace("sm:w-auto", "sm:w-full")
                : classes;
        }
    },
    watch: {
        value(isOpen) {
            if (isOpen) {
                this.onOpen();
            } else {
                this.onClose();
            }
        }
    },
    mounted() {
        if (this.value) this.onOpen();
    },
    beforeDestroy() {
        window.removeEventListener("keydown", this.onKeydown);
        this.releaseBody();
        clearTimeout(this.reboundTimer);
        if (this.appendToBody && this.$el?.parentNode === document.body) {
            document.body.removeChild(this.$el);
        }
    },
    methods: {
        onOpen() {
            window.addEventListener("keydown", this.onKeydown);
            if (!this.isCounted) {
                this.isNested = openDialogsCount > 0;
                openDialogsCount += 1;
                this.isCounted = true;
            }
            document.body.style.overflow = "hidden";
            if (this.appendToBody) this.moveToBody();
        },
        moveToBody() {
            this.$nextTick(() => {
                const el = this.$el;
                if (el && el.parentNode && el.parentNode !== document.body) {
                    document.body.appendChild(el);
                }
            });
        },
        onClose() {
            window.removeEventListener("keydown", this.onKeydown);
            this.releaseBody();
            this.$emit("close");
        },
        releaseBody() {
            if (!this.isCounted) return;
            this.isCounted = false;
            openDialogsCount = Math.max(0, openDialogsCount - 1);
            if (!openDialogsCount) document.body.style.overflow = "";
        },
        onKeydown(event) {
            if (event.key === "Escape") this.requestClose();
        },
        requestClose() {
            if (this.persistent) return this.playRebound();
            this.close();
        },
        close() {
            this.$emit("input", false);
        },
        playRebound() {
            this.rebound = true;
            clearTimeout(this.reboundTimer);
            this.reboundTimer = setTimeout(() => {
                this.rebound = false;
            }, 300);
        }
    }
};
</script>
